import { useEffect, useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Mail, Edit, DollarSign, Link2, ExternalLink, MessageSquare } from "lucide-react";
import { isOverdue } from "./InvoiceFilters";
import { CheckImageLink } from "./RecordPaymentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(n || 0).toFixed(2)}`;
const METHOD = { cash_app: "Cash App", zelle: "Zelle", stripe: "Stripe / Card", check: "Check", cash: "Cash", other: "Other", paypal: "PayPal" };
const STATUS_CLASS = { paid: "bg-green-100 text-green-800", pending: "bg-orange-100 text-orange-800", sent: "bg-blue-100 text-blue-800", partial: "bg-indigo-100 text-indigo-800", overdue: "bg-red-100 text-red-800" };

const Row = ({ label, value, cls = "" }) => (
  <div className="flex justify-between text-sm py-1"><span className="text-slate-500">{label}</span><span className={`font-medium text-slate-800 ${cls}`}>{value}</span></div>
);

export const InvoiceQuickView = ({ invoice, onClose, actions }) => {
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    setPayments([]);
    if (invoice) axios.get(`${API}/invoices/${invoice.id}/payments`).then((r) => setPayments(r.data.payments)).catch(() => {});
  }, [invoice]);
  if (!invoice) return null;
  const paid = invoice.amount_paid || 0;
  const balance = Math.max((invoice.total || 0) - paid, 0);
  const overdue = isOverdue(invoice);
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="invoice-quick-view">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <span data-testid="quick-view-invoice-number">{invoice.invoice_number}</span>
            <Badge className={STATUS_CLASS[overdue ? "overdue" : invoice.status] || STATUS_CLASS.pending} data-testid="quick-view-status">{overdue ? "OVERDUE" : invoice.status === "partial" ? "PARTIALLY PAID" : invoice.status.toUpperCase()}</Badge>
            {invoice.is_recurring && <Badge className="bg-purple-100 text-purple-800">RECURRING</Badge>}
            {!invoice.is_recurring && invoice.recurring_stopped_at && <Badge className="bg-slate-100 text-slate-600" data-testid="quick-view-recurring-stopped">Recurring stopped on {new Date(invoice.recurring_stopped_at).toLocaleDateString("en-US", { timeZone: "America/New_York" })}</Badge>}
          </SheetTitle>
          <SheetDescription>{invoice.customer_name}{invoice.technician_name ? ` · Technician: ${invoice.technician_name}` : ""}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-wrap gap-2 mt-4" data-testid="quick-view-actions">
          <Button size="sm" variant="outline" onClick={() => actions.downloadPDF(invoice.id, invoice.invoice_number)} data-testid="quick-view-download"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" onClick={() => actions.emailInvoice(invoice.id)} data-testid="quick-view-email"><Mail className="w-4 h-4 mr-1" /> Email</Button>
          {actions.textInvoice && <Button size="sm" variant="outline" onClick={() => actions.textInvoice(invoice.id)} data-testid="quick-view-text"><MessageSquare className="w-4 h-4 mr-1" /> Send by Text</Button>}
          {actions.emailAndTextInvoice && <Button size="sm" variant="outline" onClick={() => actions.emailAndTextInvoice(invoice.id)} data-testid="quick-view-email-text"><Mail className="w-4 h-4 mr-1" /> Email + Text</Button>}
          <Button size="sm" variant="outline" onClick={() => { onClose(); actions.onEdit(invoice); }} data-testid="quick-view-edit"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
          {actions.copyPayLink && <Button size="sm" variant="outline" onClick={() => actions.copyPayLink(invoice)} data-testid="quick-view-pay-link"><Link2 className="w-4 h-4 mr-1" /> Pay link</Button>}
          {actions.onRecordPayment && balance > 0 && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { onClose(); actions.onRecordPayment(invoice); }} data-testid="quick-view-record-payment"><DollarSign className="w-4 h-4 mr-1" /> Record payment</Button>}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs uppercase text-slate-500">Invoice date</p><p className="font-medium text-slate-800">{String(invoice.created_at || "").slice(0, 10)}</p></div>
          <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs uppercase text-slate-500">Due date</p><p className={`font-medium ${overdue ? "text-red-600" : "text-slate-800"}`}>{invoice.due_date}</p></div>
        </div>

        <div className="mt-5">
          <p className="text-sm font-semibold text-slate-700 mb-2">Line items</p>
          <div className="rounded-lg border divide-y" data-testid="quick-view-items">
            {(invoice.items || []).map((it, i) => (
              <div key={i} className="p-3 text-sm">
                <div className="flex justify-between gap-3">
                  <p className="text-slate-800 whitespace-pre-line flex-1">{it.description}</p>
                  <p className="font-semibold text-slate-800 whitespace-nowrap">{money(it.amount)}</p>
                </div>
                <p className="text-xs text-slate-500 mt-1">{it.quantity} × {money(it.rate)}</p>
              </div>
            ))}
            {(invoice.items || []).length === 0 && <p className="p-3 text-sm text-slate-400">No line items</p>}
          </div>
        </div>

        <div className="mt-4 rounded-lg border p-3" data-testid="quick-view-totals">
          <Row label="Subtotal" value={money(invoice.subtotal)} />
          <Row label="Tax" value={money(invoice.tax)} />
          <Row label="Total" value={money(invoice.total)} cls="text-base" />
          {paid > 0 && <Row label="Paid to date" value={`-${money(paid)}`} cls="text-green-700" />}
          <Row label="Balance due" value={money(balance)} cls={balance > 0 ? "text-red-600 text-base" : "text-green-700 text-base"} />
        </div>

        {invoice.notes && <div className="mt-4"><p className="text-sm font-semibold text-slate-700 mb-1">Notes</p><p className="text-sm text-slate-600 whitespace-pre-line rounded-lg bg-slate-50 p-3">{invoice.notes}</p></div>}

        <div className="mt-4">
          <p className="text-sm font-semibold text-slate-700 mb-2">Payments</p>
          <div className="rounded-lg border divide-y" data-testid="quick-view-payments">
            {payments.length === 0 ? <p className="p-3 text-sm text-slate-400">No payments recorded</p> : payments.map((p) => (
              <div key={p.id} className="flex justify-between items-center gap-2 p-2.5 text-sm">
                <span className="text-slate-600">{p.date} · {METHOD[p.method] || p.method}{p.check_number ? <span data-testid={`quick-view-check-number-${p.id}`}> · Check #{p.check_number}</span> : null}{p.note ? <span className="text-slate-400"> · {p.note}</span> : null}</span>
                <span className="flex items-center gap-2"><CheckImageLink payment={p} size="h-8 w-8" /><span className="font-semibold text-green-700">{money(p.amount)}</span></span>
              </div>
            ))}
          </div>
        </div>

        {invoice.stripe_invoice_url && (
          <a href={invoice.stripe_invoice_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm text-purple-700 hover:underline" data-testid="quick-view-stripe-link"><ExternalLink className="w-4 h-4" /> View Stripe invoice</a>
        )}
      </SheetContent>
    </Sheet>
  );
};
